import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QCSheetSelectorModal = ({ open, onClose, projectId, projectName }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [existingSheets, setExistingSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && projectId) {
      loadData();
    }
  }, [open, projectId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, sheetsRes] = await Promise.all([
        axios.get(`${API}/qc-sheets/templates`),
        axios.get(`${API}/qc-sheets?project_id=${projectId}`)
      ]);
      
      setTemplates(templatesRes.data || []);
      setExistingSheets(sheetsRes.data || []);
    } catch (error) {
      console.error('Failed to load QC data:', error);
      toast.error('Failed to load QC sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      const newSheet = {
        project_id: projectId,
        project_name: projectName,
        template_id: template.id,
        sheet_name: `${template.name} - ${projectName}`,
        sheet_type: template.sheet_type,
        category: template.category,
        status: 'draft',
        fields: template.fields || []
      };

      const response = await axios.post(`${API}/qc-sheets`, newSheet);
      toast.success('QC Sheet created from template');
      
      // Navigate to the new sheet detail page
      navigate(`/tools/qc-sheets/${response.data.id}`);
      onClose();
    } catch (error) {
      toast.error('Failed to create QC sheet');
    }
  };

  const handleOpenExisting = (sheetId) => {
    navigate(`/tools/qc-sheets/${sheetId}`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">QC Sheets for {projectName}</DialogTitle>
          <DialogDescription className="text-gray-700 font-medium">
            Select an existing sheet or create a new one from a template
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-gray-700 font-semibold">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Existing Sheets */}
            {existingSheets.length > 0 && (
              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-900">
                  <FileText size={18} />
                  Existing QC Sheets ({existingSheets.length})
                </h3>
                <div className="space-y-2">
                  {existingSheets.map((sheet) => (
                    <div
                      key={sheet.id}
                      className="flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => handleOpenExisting(sheet.id)}
                    >
                      <div className="flex-1">
                        <h4 className="font-bold text-sm text-gray-900">{sheet.sheet_name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs border-gray-400 text-gray-900 font-semibold">
                            {sheet.sheet_type || 'QC Sheet'}
                          </Badge>
                          <Badge
                            className={`text-xs font-bold ${
                              sheet.status === 'approved'
                                ? 'bg-green-200 text-green-900'
                                : sheet.status === 'submitted'
                                ? 'bg-blue-200 text-blue-900'
                                : 'bg-gray-300 text-gray-900'
                            }`}
                          >
                            {sheet.status}
                          </Badge>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-gray-900">
                        <ArrowRight size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Templates */}
            <div>
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-900">
                <Plus size={18} />
                Create from Template
              </h3>
              {templates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="border-2 border-gray-300 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <h4 className="font-bold text-sm mb-2 text-gray-900">{template.name}</h4>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className="text-xs border-gray-400 text-gray-900 font-semibold">
                          {template.sheet_type || 'QC Sheet'}
                        </Badge>
                        {template.category && (
                          <Badge variant="secondary" className="text-xs bg-gray-300 text-gray-900 font-semibold">
                            {template.category}
                          </Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleCreateFromTemplate(template)}
                        className="w-full font-mono text-xs font-bold"
                      >
                        <Plus size={14} className="mr-1" />
                        Create from Template
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-700 font-semibold text-center py-8">
                  No templates available. Create templates in the QC Sheets section.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QCSheetSelectorModal;
